/**
 * useFeedEngagement - Tracks comprehensive feed engagement metrics
 *
 * AIDEV-NOTE: This hook measures both passive (scroll) and active (click/reaction) engagement
 * to enable comparison between engagement types and time-to-action analysis.
 *
 * Metrics tracked:
 * - total_session_duration_seconds: Total time on feed
 * - scroll_depth_percent: Maximum scroll reached (0-100)
 * - time_to_first_passive_action_seconds: Time until first 10% scroll
 * - time_to_first_active_action_seconds: Time until first click/reaction/help
 * - first_active_action_type: Type of first active action
 * - has_passive_action: Boolean for % calculation
 * - has_active_action: Boolean for % calculation
 * - posts_seen_count: Number of posts that received impressions
 * - posts_interacted_count: Number of posts user clicked/reacted to
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAmplitudeTracking } from './useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';

// AIDEV-NOTE: Passive engagement triggers at 10% scroll depth as per plan requirements
const PASSIVE_SCROLL_THRESHOLD = 10;

export type ActiveActionType =
  'post_clicked' | 'reaction_sent' | 'help_offered' | 'bookmark' | 'share';

export interface FeedEngagementContext {
  eventFilterIds: string[];
  tab: string;
  /** Undefined when the active tab has no sort concept (Meetverse). */
  sortOrder: string | undefined;
  isAuthenticated: boolean;
}

export interface FeedEngagementState {
  sessionStartTime: number;
  maxScrollDepth: number;
  firstPassiveActionTime: number | null;
  firstActiveActionTime: number | null;
  firstActiveActionType: ActiveActionType | null;
  postsSeenIds: Set<string>;
  postsInteractedIds: Set<string>;
}

export interface FeedEngagementActions {
  /** Call when a post becomes visible (50% for 1s) */
  trackPostImpression: (postId: string) => void;
  /** Call when user performs an active action */
  trackActiveAction: (actionType: ActiveActionType, postId?: string) => void;
}

/**
 * Hook for tracking comprehensive feed engagement metrics
 *
 * @param context - Feed context (filters, sort, auth status)
 * @returns Actions to call from feed components
 */
export const useFeedEngagement = (context: FeedEngagementContext): FeedEngagementActions => {
  const { trackEvent } = useAmplitudeTracking();

  // Use lazy useState initializer for session start time (only runs once, avoids impure call during render)
  const [sessionStartTime] = useState(() => Date.now());

  // State ref to avoid re-renders and preserve mutable values across renders
  const stateRef = useRef<Omit<FeedEngagementState, 'sessionStartTime'>>({
    maxScrollDepth: 0,
    firstPassiveActionTime: null,
    firstActiveActionTime: null,
    firstActiveActionType: null,
    postsSeenIds: new Set(),
    postsInteractedIds: new Set(),
  });

  // Context ref for cleanup - synced via effect to stay React Compiler compatible
  const contextRef = useRef(context);
  useEffect(() => {
    contextRef.current = context;
  });

  // Track scroll for passive engagement
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(() => {
        rafId = null;

        const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
        const scrollHeight = document.documentElement.scrollHeight || 0;
        const viewportHeight = window.innerHeight || 0;
        const scrollable = Math.max(0, scrollHeight - viewportHeight);

        if (scrollable <= 0) return;

        const percent = Math.min(100, Math.max(0, Math.round((scrollTop / scrollable) * 100)));

        const state = stateRef.current;

        // Update max scroll depth
        if (percent > state.maxScrollDepth) {
          state.maxScrollDepth = percent;
        }

        // Track first passive action when reaching 10% threshold
        if (percent >= PASSIVE_SCROLL_THRESHOLD && state.firstPassiveActionTime === null) {
          state.firstPassiveActionTime = Date.now();
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, []);

  // Track post impression
  const trackPostImpression = useCallback((postId: string) => {
    stateRef.current.postsSeenIds.add(postId);
  }, []);

  // Track active action
  const trackActiveAction = useCallback((actionType: ActiveActionType, postId?: string) => {
    const state = stateRef.current;

    // Record first active action
    if (state.firstActiveActionTime === null) {
      state.firstActiveActionTime = Date.now();
      state.firstActiveActionType = actionType;
    }

    // Track interacted post
    if (postId) {
      state.postsInteractedIds.add(postId);
    }
  }, []);

  // Fire feed_session_ended on unmount
  useEffect(() => {
    // stateRef is a stable mutable object (never replaced), safe to capture at setup
    const state = stateRef.current;
    const startTime = sessionStartTime;
    return () => {
      // contextRef is replaced each render via sync effect, must read at cleanup time

      const ctx = contextRef.current;
      const now = Date.now();

      const sessionDuration = (now - startTime) / 1000;
      const timeToFirstPassive = state.firstPassiveActionTime
        ? (state.firstPassiveActionTime - startTime) / 1000
        : null;
      const timeToFirstActive = state.firstActiveActionTime
        ? (state.firstActiveActionTime - startTime) / 1000
        : null;

      trackEvent(ANALYTICS_EVENTS.feed_session_ended, {
        // Session duration
        total_session_duration_seconds: Math.round(sessionDuration),

        // Passive engagement
        scroll_depth_percent: state.maxScrollDepth,
        time_to_first_passive_action_seconds: timeToFirstPassive
          ? Math.round(timeToFirstPassive)
          : null,
        has_passive_action: state.maxScrollDepth >= PASSIVE_SCROLL_THRESHOLD,

        // Active engagement
        time_to_first_active_action_seconds: timeToFirstActive
          ? Math.round(timeToFirstActive)
          : null,
        first_active_action_type: state.firstActiveActionType,
        has_active_action: state.firstActiveActionTime !== null,

        // Content
        posts_seen_count: state.postsSeenIds.size,
        posts_interacted_count: state.postsInteractedIds.size,

        // Context
        tab: ctx.tab,
        event_filter_ids: ctx.eventFilterIds.join(','),
        is_authenticated: ctx.isAuthenticated,
        ...(ctx.sortOrder ? { sort_order: ctx.sortOrder } : {}),
      });
    };
  }, [trackEvent, sessionStartTime]); // Fire on unmount (sessionStartTime is stable from lazy init)

  return {
    trackPostImpression,
    trackActiveAction,
  };
};
